import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchLogs, fetchDevices, fetchMerchants, clearError } from '../../redux/ActionLogs';
import { useTheme } from '../../context/ThemeContext';
import { ThemeProvider } from 'styled-components';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { ConfigProvider } from 'antd';
import moment from 'moment';
import DateRangePicker from '../../commonComponents/DateRangePicker';
import LogsTable from './LogsTable';
import LogDetailsModal from './LogDetailsModal';
import {
  Container,
  HeaderContainer,
  Title,
  FiltersContainer,
  FilterItem,
  StyledSelect,
  EmptyText,
  StyledButton,
  ErrorContainer,
  ErrorText,
} from './Styles';
import { ERROR_MESSAGES, EMPTY_STATES, DEFAULT_PAGE_SIZE } from './constants';
import { Log, DateRange } from './types';

const ActionLogs: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { logs, devices, merchants, totalItems, loading, error } = useSelector(
    (state: RootState) => state.actionLogs
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment(),
    endDate: moment(),
  });
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>('');
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const user = localStorage.getItem('user');
  const parsedUser = useMemo(() => {
    if (!user) return null;
    try {
      return JSON.parse(user);
    } catch {
      return null;
    }
  }, [user]);

  const isAdmin = parsedUser?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchMerchants());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    dispatch(fetchDevices({ merchantId: selectedMerchantId || undefined }));
  }, [dispatch, selectedMerchantId]);

  useEffect(() => {
    const params: any = {
      page: currentPage,
      limit: pageSize,
    };

    if (isAdmin && selectedMerchantId) {
      params.merchantId = selectedMerchantId;
    }

    if (selectedDeviceId) {
      params.deviceId = selectedDeviceId;
    }

    if (selectedDateRange.startDate && selectedDateRange.endDate) {
      params.startDate = selectedDateRange.startDate.format('YYYY-MM-DD');
      params.endDate = selectedDateRange.endDate.format('YYYY-MM-DD');
    }

    dispatch(fetchLogs(params));
  }, [dispatch, currentPage, pageSize, selectedDateRange, selectedDeviceId, selectedMerchantId, isAdmin]);

  const handleDateRangeChange = (range: DateRange) => {
    setSelectedDateRange(range);
    setCurrentPage(1);
  };

  const handleDeviceChange = (value: string) => {
    setSelectedDeviceId(value);
    setCurrentPage(1);
  };

  const handleMerchantChange = (value: string) => {
    setSelectedMerchantId(value);
    setSelectedDeviceId('');
    setCurrentPage(1);
  };

  const handleLogClick = (log: Log) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedLog(null);
  };

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const theme: { mode: 'light' | 'dark' } = {
    mode: isDarkMode ? 'dark' : 'light',
  };

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderContainer>
            <Title level={2}>Logs</Title>
          </HeaderContainer>

          {error && (
            <ErrorContainer>
              <ErrorText>{error || ERROR_MESSAGES.GENERIC}</ErrorText>
              <StyledButton onClick={() => dispatch(clearError())}>Retry</StyledButton>
            </ErrorContainer>
          )}

          <FiltersContainer>
            <FilterItem>
              <DateRangePicker
                selectedDateRange={selectedDateRange}
                onDateRangeChange={handleDateRangeChange}
              />
            </FilterItem>

            {isAdmin && (
              <FilterItem>
                {merchants?.length > 0 ? (
                  <StyledSelect
                    placeholder="All Merchants"
                    value={selectedMerchantId || undefined}
                    onChange={handleMerchantChange}
                    allowClear
                  >
                    {merchants?.map((merchant) => (
                      <StyledSelect.Option key={merchant.id} value={merchant.id}>
                        {merchant?.name}
                      </StyledSelect.Option>
                    ))}
                  </StyledSelect>
                ) : (
                  <EmptyText>{EMPTY_STATES.NO_MERCHANTS}</EmptyText>
                )}
              </FilterItem>
            )}

            <FilterItem>
              {devices?.length > 0 ? (
                <StyledSelect
                  placeholder="All Devices"
                  value={selectedDeviceId || undefined}
                  onChange={handleDeviceChange}
                  allowClear
                >
                  {devices?.map((device) => (
                    <StyledSelect.Option key={device.id} value={device.id}>
                      {device?.name}
                    </StyledSelect.Option>
                  ))}
                </StyledSelect>
              ) : (
                <EmptyText>{EMPTY_STATES.NO_DEVICES}</EmptyText>
              )}
            </FilterItem>
          </FiltersContainer>

          <LogsTable
            logs={logs}
            loading={loading}
            isAdmin={isAdmin}
            isDarkMode={isDarkMode}
            totalItems={totalItems}
            currentPage={currentPage}
            pageSize={pageSize}
            onLogClick={handleLogClick}
            onPaginationChange={handlePaginationChange}
          />

          <LogDetailsModal
            isOpen={isModalOpen}
            selectedLog={selectedLog}
            isAdmin={isAdmin}
            onClose={handleModalClose}
            theme={theme}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default ActionLogs;
